import { Component, EventEmitter, Input, Output, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';
import { AjaxService } from '../../../../../services/ajax.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';
// PrimeNG
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { ReactiveFormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';

import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';

import { forkJoin } from 'rxjs';


import { LOT_CREDIT_TYPES, LOT_BANK_NAMES, LOT_PAYMENT_TYPE } from '../../../../../services/account-api.constants';

export interface PaymentInfo {
	id: string;
	description: string;
	isApproved: number;
	isActive: number;

	paymentTypeRecId: string;
	paymentTypeCode?: string;
	type: string; // label

	bank_account_number: string;
	bank_branch: string;
	bank_name: string;   // internal RecID/ID
	bank_number: string; // internal code
	bankLabel?: string;  // user-friendly

	bank_check_number: string;

	credit_card_number: string;
	creditCardTypeRecId: string;
	creditCardLabel?: string; // user-friendly

	paymentAccountNumber: string;
	uniqueFeature: boolean;
}


interface SelectItem {
	label: string;
	value: string;
	code?: string;
}

@Component({
	selector: 'app-payment-info',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		InputTextModule,
		ReactiveFormsModule,
		ButtonModule,
		TagModule,
		ToastModule,
		AutoCompleteModule,
		ProgressSpinnerModule,
		TableModule,
		DropdownModule
	],
	templateUrl: './payment-info.component.html',
	styleUrls: ['./payment-info.component.scss'],
	providers: [MessageService]
})
export class PaymentInfoComponent implements OnInit {
	@Input() paymentInfo: PaymentInfo[] = [];
	@Output() paymentInfoChange = new EventEmitter<PaymentInfo[]>();
	@Output() nextStep = new EventEmitter<void>();
	@Output() prevStep = new EventEmitter<void>();

	@Input() customerRecId!: number;


	readonly DESCRIPTION_MAX_LENGTH = 250;
	isDialogMode = false;
	isCreate = false;
	loading = false;

	CREDIT_TYPE_CODE = '100';
	BANK_TYPE_CODE = '110';


	newEntry: PaymentInfo = this.createEmptyEntry();

	paymentTypeOptions: SelectItem[] = [];
	creditCardTypeOptions: SelectItem[] = [];
	bankNameOptions: SelectItem[] = [];




	// Filtered lists for autocomplete
	filteredCreditCardTypes: any[] = [];
	filteredBankNames: any[] = [];
	filteredPaymentTypes: any[] = [];

	constructor(
		private messageService: MessageService,
		public translateServ: TranslateService,
		public dirServ: DirectionService,
		private ajaxService: AjaxService,
		private viewScopeService: ViewScopeService,
		@Optional() public ref: DynamicDialogRef,
		@Optional() public config: DynamicDialogConfig,
		@Optional() private maintainAccountService: MaintainAccountService
	) { }

	ngOnInit(): void {
		this.loading = true;
		const paymentTypes$ = this.ajaxService.getData(
			this.ajaxService.getBaseOfApi() + LOT_PAYMENT_TYPE + this.viewScopeService.getCurrentOpertaingUserId().toString()
		);
		const banks$ = this.ajaxService.getData(
			this.ajaxService.getBaseOfApi() + LOT_BANK_NAMES + this.viewScopeService.getCurrentOpertaingUserId().toString()
		);
		const creditCards$ = this.ajaxService.getData(
			this.ajaxService.getBaseOfApi() + LOT_CREDIT_TYPES + this.viewScopeService.getCurrentOpertaingUserId().toString()
		);

		forkJoin([paymentTypes$, banks$, creditCards$]).subscribe({
			next: ([types, banks, cards]) => {
				// build arrays
				this.paymentTypeOptions = (types as any[]).map((item: any) => ({
					label: item.PaymentType,
					value: String(item.RecID),
					code: String(item.Code)
				}));
				this.bankNameOptions = (banks as any[]).map((item: any) => ({
					label: item.OrganizationName,
					value: String(item.IdentificationCodeRecID),
					code: String(item.Code)
				}));
				this.creditCardTypeOptions = (cards as any[]).map((item: any) => ({
					label: item.CreditCardType,
					value: String(item.CreditCardTypeRecID)
				}));

				// now safe to assign entries
				if (this.config && this.config.data) {
					this.isDialogMode = this.config.data.isDialog;
					this.customerRecId = this.config.data.customerRecId;
					this.isCreate = this.config.data.isCreate;

					if (!this.isCreate && this.config.data.nameEntry) {
						this.paymentInfo = [this.config.data.nameEntry];
						this.paymentInfo.forEach(entry => {
							if (!entry.type && entry.paymentTypeRecId) {
								const found = this.paymentTypeOptions.find(opt => opt.value === entry.paymentTypeRecId.toString());
								if (found) {
									entry.type = found.label;
									entry.paymentTypeCode = found.code;
								}
							}
							if (entry.bank_number) {
								const found = this.bankNameOptions.find(opt => opt.code === entry.bank_number.toString());
								if (found) {
									entry.bank_name = found.label;
									entry.bankLabel = found.label;
								}
							}
							if (entry.creditCardTypeRecId) {
								const found = this.creditCardTypeOptions.find(opt => opt.value === entry.creditCardTypeRecId.toString());
								if (found) {
									entry.description = found.label;
									entry.creditCardLabel = found.label;
								}
							}
						});
					}
				}
			},
			complete: () => {
				this.loading = false;
			}
		});
	}

	private generateUniqueId(): string {
		return 'id_' + Math.random().toString(36).substring(2, 9);
	}

	private createEmptyEntry(): PaymentInfo {
		return {
			id: this.generateUniqueId(),
			paymentTypeCode: '',
			description: '',
			isApproved: 1,
			isActive: 1,
			type: '',
			paymentTypeRecId: '',
			bank_account_number: '',
			bank_branch: '',
			bank_name: '',
			bank_number: '',
			bank_check_number: '',
			credit_card_number: '',
			creditCardTypeRecId: '',
			paymentAccountNumber: '',
			uniqueFeature: false
		};
	}

	resetNewEntry(): void {
		this.newEntry = this.createEmptyEntry();
	}

	addEntry(): void {
		if (!this.newEntry.paymentTypeRecId) {
			this.messageService.add({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Payment Type is required.',
				life: 3000
			});
			return;
		}

		if (this.newEntry.description && this.newEntry.description.length > this.DESCRIPTION_MAX_LENGTH) {
			this.messageService.add({
				severity: 'error',
				summary: 'Validation Error',
				detail: `Description exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
				life: 3000
			});
			return;
		}

		if (!this.newEntry.paymentAccountNumber) {
			this.messageService.add({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Payment Account Number is required.',
				life: 3000
			});
			return;
		}

		this.paymentInfo.push({ ...this.newEntry });
		this.emitChanges();

		if (this.isCreate) this.resetNewEntry();

		this.messageService.add({
			severity: 'success',
			summary: 'Added',
			detail: 'New payment entry added.',
			life: 1500
		});
	}

	removeEntry(entryToRemove: PaymentInfo): void {
		this.paymentInfo = this.paymentInfo.filter(entry => entry.id !== entryToRemove.id);
		this.emitChanges();
		this.messageService.add({
			severity: 'info',
			summary: 'Removed',
			detail: 'Payment entry removed.',
			life: 1500
		});
	}

	emitChanges(): void {
		this.paymentInfoChange.emit(this.paymentInfo);
	}

	getRemainingChars(description: string): number {
		return this.DESCRIPTION_MAX_LENGTH - (description?.length || 0);
	}

	validateAndProceed(): void {
		let isValid = true;

		for (const entry of this.paymentInfo) {
			if (!entry.paymentTypeRecId) {
				isValid = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Validation Error',
					detail: 'Payment Type is required for all entries.',
					life: 3000
				});
				break;
			}

			if (entry.description && entry.description.length > this.DESCRIPTION_MAX_LENGTH) {
				isValid = false;
				this.messageService.add({
					severity: 'error',
					summary: 'Validation Error',
					detail: `Description for entry "${entry.id}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
					life: 3000
				});
				break;
			}
		}

		if (isValid) {
			this.nextStep.emit();
			this.messageService.clear();
		}
	}

	goToPreviousStep(): void {
		this.prevStep.emit();
	}

	applyRowChanges(entry: PaymentInfo): void {
		this.emitChanges();
		this.messageService.add({
			severity: 'info',
			summary: 'Changes Applied',
			detail: `Changes applied for "${entry.paymentTypeRecId}". Remember to press Save to persist.`,
			life: 2500
		});
	}

	saveAndSubmit(): void {
		if (!this.maintainAccountService) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Warning',
				detail: 'This functionality is not available in standalone mode.',
				life: 3000
			});
			return;
		}

		const requests = this.paymentInfo.map(entry => {
			const basePayload: any = {
				PaymentTypeRecID: entry.paymentTypeRecId,
				bank_account_number: entry.bank_account_number,
				bank_branch: entry.bank_branch,
				bank_name: entry.bank_name,
				bank_number: entry.bank_number,
				BankCheckNum: entry.bank_check_number,
				CreditCardNum: entry.credit_card_number,
				CreditCardTypeRecID: entry.creditCardTypeRecId,
				PaymentAccountNum: entry.paymentAccountNumber,
				UniqueFeature: entry.uniqueFeature ? 1 : 0,
				Descr: entry.description || '',
				IsActive: entry.isActive,
				IsApproved: entry.isApproved,
				UserRecID: this.viewScopeService.getCurrentOpertaingUserId()
			};
						
			if (this.isCreate) 
			{
				basePayload.CustomerSupplierRecID = this.customerRecId;
			} 
			else 
			{
				basePayload.RecID = entry.id;
			}

			return basePayload;
		});

		console.log('Data we will save:', requests);

		requests.forEach(record => {
			this.maintainAccountService
				.insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1), JSON.stringify(record))
				.subscribe({
					next: response => {
						this.messageService.add({
							severity: 'success',
							summary: 'Success',
							detail: `Saved record for "${record.PaymentTypeRecID}".`,
							life: 2000
						});

						if (this.ref && record === requests[requests.length - 1]) {
							this.ref.close(response);
						}
					},
					error: () => {
						this.messageService.add({
							severity: 'error',
							summary: 'Error',
							detail: `Failed to save record for "${record.PaymentTypeRecID}".`,
							life: 3000
						});
					},
					complete: () => {
						this.messageService.add({
							severity: 'success',
							summary: 'Complete',
							detail: 'All records processed.',
							life: 2000
						});
						if (this.ref) {
							this.ref.close(true);
						}
					}
				});
		});
	}

	getSeverity(val: boolean): string {
		return val ? 'success' : 'danger';
	}

	toggleIsActive(entry: any): void {
		entry.isActive = entry.isActive === 1 ? 0 : 1;
	}

	toggleIsApproved(entry: any): void {
		entry.isApproved = entry.isApproved === 1 ? 0 : 1;
	}

	toggleUnique(entry: PaymentInfo): void {
		entry.uniqueFeature = !entry.uniqueFeature;
	}


	closeDialog(shouldReload: boolean = false): void {
		if (this.ref) {
			this.ref.close(shouldReload);
		}
	}


	onBankSelected(event: any, entry: PaymentInfo) {
		if (event && event.value) {
			entry.bankLabel = event.value.label; // visible text
			entry.bank_number = event.value.value; // internal ID
			entry.bank_name = event.value.label; // store text as well
		}
	}

	onCreditCardTypeSelected(event: any, entry: PaymentInfo) {
		if (event && event.value) {
			entry.creditCardLabel = event.value.label; // visible text
			entry.creditCardTypeRecId = event.value.value; // internal ID
		}
	}

	onPaymentTypeChange(event: any, entry: PaymentInfo) {
		if (event && event.value) {
			entry.paymentTypeRecId = event.value.value;
			entry.type = event.value.label;
			entry.paymentTypeCode = event.value.code;

			if (entry.paymentTypeCode === this.BANK_TYPE_CODE) {
				entry.creditCardTypeRecId = '';
				entry.creditCardLabel = '';
				entry.credit_card_number = '';
			} else if (entry.paymentTypeCode === this.CREDIT_TYPE_CODE) {
				entry.bankLabel = '';
				entry.bank_name = '';
				entry.bank_branch = '';
				entry.bank_account_number = '';
			} else {
				entry.bankLabel = entry.bank_name = '';
				entry.bank_branch = entry.bank_account_number = '';
				entry.creditCardTypeRecId = '';
				entry.creditCardLabel = '';
				entry.credit_card_number = '';
			}
		}
	}





	// Handles autocomplete for credit card types
	searchCreditCardTypes(event: any) {
		const query = event.query.toLowerCase();
		this.filteredCreditCardTypes = this.creditCardTypeOptions.filter(opt =>
			opt.label.toLowerCase().includes(query)
		);
	}

	// Handles autocomplete for bank names
	searchBankNames(event: any) {
		const query = event.query.toLowerCase();
		this.filteredBankNames = this.bankNameOptions.filter(opt =>
			opt.label.toLowerCase().includes(query)
		);
	}

	searchPaymentTypes(event: any) {
		const query = event.query.toLowerCase();
		this.filteredPaymentTypes = this.paymentTypeOptions.filter(
			opt => opt.label.toLowerCase().includes(query)
		);
	}

}
